import styled from 'styled-components';
import { color } from 'constants/styled-theme';
import PropTypes from 'prop-types';
import ReactSelect from 'react-select';
import { LinkStyleButton } from './button';

const DEFAULT_MAXIMUM_PAGINATION_PAGES = 8;
const DEFAULT_SIZE_PER_PAGE = 5;

const StyledPaginationWrap = styled.div`
  font-size: 18px;
  font-family: ${props => props.theme.fonts.regular};
  display: flex;
  @media (max-width: ${p => p.theme.mobile}) {
    display: none;
  }
`;
const StyledButton = styled(LinkStyleButton)`
  font-size: 18px;
  font-family: ${props => props.theme.fonts.regular};
  padding: 2px 5px;
  line-height: 18px;
  border: none;
  background: none;
  text-decoration: none;
  //display: ${p => (p.isHeaderBar && p.disabled ? 'none' : 'flex')};
  color: ${color('fontColor')};
  //color: ${p => (p.blue ? color('blue25') : color('fontColor'))};
  opacity: ${p => (p.disabled ? 0.5 : 1)};
  span {
    font-size: 18px;
  }
  @media (max-width: ${p => p.theme.mobile}) {
    font-size: 10px;
    padding: 0 5px;
  }
`;

const StyledButtonNumber = styled(StyledButton)`
  background: ${p => (p.current ? color('gray30') : color('gray0'))};
  opacity: ${p => (p.current ? 1 : 0.5)};
  font-weight: ${p => (p.current ? p.theme.fonts.semiBold : p.theme.fonts.regular)};
  font-size: 18px;
  padding: 2px 10px;
`;

const StyledPaginationCol = styled.div`
  width: 100%;
  font-family: ${props => props.theme.fonts.regular};
  text-align: right;
`;

const mobileStyles = {
  control: s => ({
    ...s,
    backgroundColor: '#f8f3f3',
    color: color('gray100'),
    borderRadius: '0',
    padding: 0,
    border: 'none',
    boxShadow: 'none',
    fontSize: '7px',
    minHeight: '15px',
    flexWrap: 'nowrap'
  }),
  singleValue: s => ({
    ...s,
    color: 'black',
    fontSize: '7px'
  }),
  placeholder: s => ({
    ...s,
    color: 'black',
    fontSize: '7px'
  }),
  indicatorSeparator: s => ({
    ...s,
    display: 'none'
  }),
  indicatorsContainer: s => ({
    ...s,
    color: color('gray100'),
    padding: 0
  }),
  dropdownIndicator: s => ({
    ...s,
    color: color('gray100'),
    padding: 0
  }),
  input: s => ({ ...s, margin: 0, padding: 0 }),
  valueContainer: s => ({ ...s, margin: 0, padding: 0 }),
  menu: s => ({ ...s, zIndex: 10, minWidth: '60px' }),
  menuList: s => ({ ...s, fontSize: '7px' })
};
const SelectWrapper = styled.div`
  margin: 0 20px;
  display: none;
  flex-grow: 1;
  align-items: center;
  @media (max-width: ${p => p.theme.mobile}) {
    display: flex;
  }
`;

const Select = styled(ReactSelect)`
  border: none;
  color: ${color('fontColor')};
  flex-grow: 1;
  border-radius: 0;
  svg {
    color: ${color('gray100')};
    height: 10px;
    width: 10px;
    &:hover {
      color: ${color('gray100')};
      opacity: 0.9;
    }
  }
`;

const SubTitle = styled.div`
  margin-left: 5px;
  font-size: 8px;
  font-family: ${props => props.theme.fonts.semiBold};
`;

const SelectPage = ({ totalRecords, numberPerPage, currentPage, setCurrentPage }) => {
  const itemPerPage = numberPerPage || DEFAULT_SIZE_PER_PAGE;
  const numPages = (totalRecords && Math.ceil(totalRecords / itemPerPage)) || 0;
  const getPageNumbers = () => {
    if (numPages > 0) {
      const delta = DEFAULT_MAXIMUM_PAGINATION_PAGES / 2;
      const left = currentPage - delta;
      const right =
        currentPage <= delta ? DEFAULT_MAXIMUM_PAGINATION_PAGES : currentPage + delta + 1;

      return Array.from({ length: numPages }, (v, k) => k + 1).filter(
        i => i && i >= left && i < right
      );
    }
    return [];
  };
  const options = getPageNumbers().map(i => ({ value: i, label: i }));
  return numPages > 1 ? (
    <SelectWrapper>
      <SubTitle>Select Page</SubTitle>
      <Select
        options={options}
        value={{ value: currentPage, label: currentPage }}
        styles={mobileStyles}
        onChange={o => setCurrentPage(o.value)}
      />
    </SelectWrapper>
  ) : null;
};

export const Pagination = ({
  totalRecords,
  numberPerPage,
  currentPage,
  setCurrentPage,
  isHeaderBar
}) => {
  const itemPerPage = numberPerPage || DEFAULT_SIZE_PER_PAGE;
  const numPages = (totalRecords && Math.ceil(totalRecords / itemPerPage)) || 0;

  const getPageNumbers = () => {
    if (numPages > 0) {
      const delta = DEFAULT_MAXIMUM_PAGINATION_PAGES / 2;
      const left = currentPage - delta;
      const right =
        currentPage <= delta ? DEFAULT_MAXIMUM_PAGINATION_PAGES : currentPage + delta + 1;

      return Array.from({ length: numPages }, (v, k) => k + 1).filter(
        i => i && i >= left && i < right
      );
    }
    return [];
  };

  return (
    <>
      <SelectPage
        totalRecords={totalRecords}
        currentPage={currentPage}
        numberPerPage={numberPerPage}
        setCurrentPage={setCurrentPage}
      />
      <StyledPaginationWrap>
        {numPages > 1 && (
          <StyledPaginationCol>
            <StyledButton
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage - 1 === 0}
            >
              {`< ${isHeaderBar ? '' : 'Previous'}`}
            </StyledButton>

            {getPageNumbers().map(num => (
              <StyledButtonNumber
                disabled={currentPage === num}
                current={currentPage === num}
                onClick={() => setCurrentPage(num)}
                key={`pagination_key_${num}`}
              >
                {num}
              </StyledButtonNumber>
            ))}

            {currentPage + 1 < numPages && (
              <StyledButton onClick={() => setCurrentPage(numPages)}>...</StyledButton>
            )}
            <StyledButton
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage + 1 > numPages}
            >
              {`${isHeaderBar ? '' : 'Next'} >`}
            </StyledButton>
          </StyledPaginationCol>
        )}
      </StyledPaginationWrap>
    </>
  );
};

Pagination.propTypes = {
  totalRecords: PropTypes.number,
  numberPerPage: PropTypes.number,
  currentPage: PropTypes.number,
  setCurrentPage: PropTypes.func,
  isHeaderBar: PropTypes.bool
};

SelectPage.propTypes = {
  totalRecords: PropTypes.number,
  numberPerPage: PropTypes.number,
  currentPage: PropTypes.number,
  setCurrentPage: PropTypes.func
};
